import './index.css';
import BotItem from './BotItem';
import { SortingToggleOptions, SortingDirection } from '../types';
import Toggles from '../../../components/Toggles';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Transaction } from 'grts-utils/src/types/Transaction';
import { SubscriptionSummary } from '../../../types/subscription';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import {
  selectSorting,
  selectSortingDirection,
  setSorting,
  setSortingDirection,
} from '../../../store/slices/ui';
import { colors } from '../../../colors';
import useWidthBreakpoint from '../../../hooks/useWidthBreakpoint';

interface Props {
  totalValue: number;
  subscriptions: SubscriptionSummary[];
  transactions: Transaction[];
}

const toggleOptions = [
  SortingToggleOptions.TotalValue,
  SortingToggleOptions.TotalStableProfit,
  SortingToggleOptions.TotalAssetProfit,
  SortingToggleOptions.CurrentStableProfit,
  SortingToggleOptions.CurrentAssetProfit,
  SortingToggleOptions.NoAlgorithmicDifference,
];

const ClusterItem: React.FC<Props> = (props: Props) => {
  const { subscriptions } = props;

  const dispatch = useDispatch();
  const { breakpoint } = useWidthBreakpoint();
  const listRef = useRef<HTMLDivElement | null>(null);

  const activeToggleOption = useSelector(selectSorting);
  const sortingDirection = useSelector(selectSortingDirection);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSortingPress = useCallback(
    (toggleOption: SortingToggleOptions) => {
      if (activeToggleOption === toggleOption) {
        dispatch(
          setSortingDirection(
            sortingDirection === SortingDirection.Descending
              ? SortingDirection.Ascending
              : SortingDirection.Descending,
          ),
        );
      } else {
        dispatch(setSorting(toggleOption));
      }
    },
    [activeToggleOption, dispatch, sortingDirection],
  );

  const sortedSubscriptions = useMemo(() => {
    const subscriptionsToSort = [...subscriptions];

    switch (activeToggleOption) {
      case SortingToggleOptions.TotalValue:
      case SortingToggleOptions.Diversity:
        return subscriptionsToSort.sort((a, b) =>
          sortingDirection === SortingDirection.Descending
            ? b.totalBalanceInStable - a.totalBalanceInStable
            : a.totalBalanceInStable - b.totalBalanceInStable,
        );

      case SortingToggleOptions.TotalStableProfit:
        return subscriptionsToSort.sort((a, b) =>
          sortingDirection === SortingDirection.Descending
            ? (b.profits?.total.stable.relative ?? 0) -
              (a.profits?.total.stable.relative ?? 0)
            : (a.profits?.total.stable.relative ?? 0) -
              (b.profits?.total.stable.relative ?? 0),
        );

      case SortingToggleOptions.TotalAssetProfit:
        return subscriptionsToSort.sort((a, b) =>
          sortingDirection === SortingDirection.Descending
            ? (b.profits?.total.asset.relative ?? 0) -
              (a.profits?.total.asset.relative ?? 0)
            : (a.profits?.total.asset.relative ?? 0) -
              (b.profits?.total.asset.relative ?? 0),
        );

      case SortingToggleOptions.CurrentStableProfit:
        return subscriptionsToSort.sort((a, b) => {
          const stableProfitA =
            a.latestTransaction?.orderType === 'BUY' ? a.currentProfit : 0;
          const stableProfitB =
            b.latestTransaction?.orderType === 'BUY' ? b.currentProfit : 0;

          return sortingDirection === SortingDirection.Descending
            ? (stableProfitB || 0) - (stableProfitA || 0)
            : (stableProfitA || 0) - (stableProfitB || 0);
        });

      case SortingToggleOptions.CurrentAssetProfit:
        return subscriptionsToSort.sort((a, b) => {
          const stableProfitA =
            a.latestTransaction?.orderType === 'SELL' ? a.currentProfit : 0;
          const stableProfitB =
            b.latestTransaction?.orderType === 'SELL' ? b.currentProfit : 0;

          return sortingDirection === SortingDirection.Descending
            ? (stableProfitB || 0) - (stableProfitA || 0)
            : (stableProfitA || 0) - (stableProfitB || 0);
        });

      case SortingToggleOptions.NoAlgorithmicDifference:
        return subscriptionsToSort.sort((a, b) =>
          sortingDirection === SortingDirection.Descending
            ? (b.profits?.noAlgorithmicProgressionProfit.relative ?? 0) -
              (a.profits?.noAlgorithmicProgressionProfit.relative ?? 0)
            : (a.profits?.noAlgorithmicProgressionProfit.relative ?? 0) -
              (b.profits?.noAlgorithmicProgressionProfit.relative ?? 0),
        );
    }

    return subscriptionsToSort;
  }, [activeToggleOption, subscriptions, sortingDirection]);

  return (
    <div
      className="ci-root no-scrollbar"
      style={{ overflowY: isModalOpen ? 'hidden' : 'auto' }}
    >
      {/** @TODO This should only be visible for dev and be bots, not subs, probably also separate tab */}
      {/* <NextOrderList bots={bots} /> */}

      <div
        className="overview-cluster-bots-sort-container"
        style={{
          paddingTop: breakpoint === 'sm' ? 24 : 38,
          marginBottom: breakpoint === 'sm' ? 0 : 24,
          position: 'sticky',
          top: 0,
          backgroundColor: colors.black,
          zIndex: 5,
          paddingRight: breakpoint === 'sm' ? 16 : 0,
        }}
      >
        <p
          className="overview-cluster-bots-sort-text"
          style={{ display: breakpoint === 'sm' ? 'none' : 'block' }}
        >
          Sort By:
        </p>
        <Toggles
          options={toggleOptions}
          activeOption={activeToggleOption}
          handleOptionPress={handleSortingPress}
          sortingDirection={sortingDirection}
        />
      </div>

      <div className="ci-itemList" ref={listRef}>
        {sortedSubscriptions.map((sub, index) => (
          <BotItem
            key={sub.id}
            subscription={sub}
            activeToggleOption={activeToggleOption}
            parentRef={listRef}
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            delayIndex={index}
          />
        ))}
      </div>
    </div>
  );
};

export default ClusterItem;
